import { useCallback } from "react";
import { atom, PrimitiveAtom, useAtom } from "jotai";

import { Api, DataFile } from "interfaces";
import { useGetUserDetails } from "auth/auth-state";

import {
  useGetTypesForFile,
  useGetDroppedColumns,
} from "modules/data-files/api";
import { useCreateProjectWithDataAndWorkspace } from "modules/projects/api";

import * as flowConfig from "flows/network-failure-analysis/config";

import { useGetPerfFile, useGetSiteFile } from "./store";

const projectCreateStatus = atom<"not-started" | "in-progress" | "completed">(
  "not-started"
);

const projectCreateError = atom<string | Api.ApiError>(null) as PrimitiveAtom<
  string | Api.ApiError
>;

export const useGetCreate = () => {
  const createProject = useCreateProjectWithDataAndWorkspace();

  const perfFile = useGetPerfFile();
  const perfColTypes = useGetTypesForFile(perfFile);
  const droppedPerfColumns = useGetDroppedColumns(perfFile);

  const siteFile = useGetSiteFile();
  const siteColTypes = useGetTypesForFile(siteFile);
  const droppedSiteColumns = useGetDroppedColumns(siteFile);

  const [status, setStatus] = useAtom(projectCreateStatus);
  const [error, setError] = useAtom(projectCreateError);

  const user = useGetUserDetails()?.email;

  const create = useCallback(async () => {
    if (!perfFile) {
      setError("Performance Data is MANDATORY!!!");
      return;
    }

    setStatus("in-progress");
    setError(null);

    const projectSpec = {
      flow: flowConfig.FLOW_NAME,
      data_files: {
        [flowConfig.PERFORMANCE_DATA]: perfFile,
        [flowConfig.LOCATION_DATA]: siteFile,
      },
      meta_data: { ...flowConfig.VARIABLE_MAPPING },
    };

    const siteTypes: DataFile.UserDefinedTypes = Object.fromEntries(
      Object.entries(siteColTypes).map(([col, type]) => [
        col,
        col === flowConfig.VARIABLE_MAPPING[flowConfig.SITE_DATA_LAT_KEY][0] ||
        col === flowConfig.VARIABLE_MAPPING[flowConfig.SITE_DATA_LONG_KEY][0]
          ? type
          : "char",
      ])
    );

    const performanceWSSpec = {
      key: "INIT",
      meta_data: {},
      annotations: {
        [flowConfig.RUN_ID]: "INIT",
        [flowConfig.CREATED_BY]: user,
        [flowConfig.TIME_STAMP]: new Date().valueOf(),
      },
      recipe: flowConfig.FAILURE_CHANCES_RECIPE,
      input_data: {
        [flowConfig.PERFORMANCE_DATA]: [perfFile],
        [flowConfig.LOCATION_DATA]: [siteFile],
      },
      cleanup_spec: {
        [perfFile]: {
          column_types: perfColTypes,
          columns_to_drop: droppedPerfColumns,
        },
        [siteFile]: {
          column_types: siteTypes,
          columns_to_drop: droppedSiteColumns,
        },
      },
      variable_mapping: flowConfig.VARIABLE_MAPPING,
      model_params: flowConfig.DEFAULT_MODEL_PARAMS,
      hyper_params: {},
    };

    const resp = await createProject({
      "project-spec": projectSpec,
      "workspace-spec": [performanceWSSpec],
    });
    if (resp.success) {
    } else if (resp.success === false) {
      setError(resp.error);
    }
    setStatus("completed");
    return resp;
  }, [
    siteFile,
    perfFile,
    user,
    perfColTypes,
    droppedPerfColumns,
    siteColTypes,
    droppedSiteColumns,
    createProject,
  ]);

  return [create, status, error] as const;
};
